import { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from '../helpers/Axios';
import setAuthHeaders from '../helpers/SetAuthHeaders';
import { apiPath } from '../constants/defaultValues';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user_data');
    return saved ? JSON.parse(saved) : null;
  });
  const [multipleRoles, setMultipleRoles] = useState(() => {
    const saved = localStorage.getItem('multiple_roles');
    return saved ? JSON.parse(saved) : null;
  });

  // Restore auth headers on every mount (page refresh / browser reopen).
  // user_data in localStorage is the source of truth for the token — the
  // session cookie alone isn't enough because it's cleared when the browser
  // is fully closed.
  useEffect(() => {
    const saved = localStorage.getItem('user_data');
    if (!saved) return;
    try {
      const userObj = JSON.parse(saved);
      if (userObj?.key) {
        setAuthHeaders({
          authToken: userObj.key,
          userID: userObj.user_id,
          roleID: userObj.role_id,
        });
      }
    } catch {
      // corrupted localStorage — user will need to re-login
    }
  }, []);

  // -------------------------------------------------------------
  // LOGIN USING EMAIL + PASSWORD
  // -------------------------------------------------------------
  const loginWithCredentials = async (email, password) => {
    setAuthLoading(true);
    setAuthError('');

    try {
      const res = await axios.post(`https://api.fortisph.space/gateway/authentication/ost/token/`, {
        username: email,
        password,
      },
        {
          headers: {
            'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
          }
        });

      const data = res.data;

      // eSecure detection flow
      if (data.keycloak_id) {
        setAuthError('An eSECURE account was detected. Please log in using the eSECURE Portal.');
        setAuthLoading(false);
        return;
      }

      if (Array.isArray(data) && data.length > 1) {
        localStorage.setItem('multiple_roles', JSON.stringify(data));
        setMultipleRoles(data);
      }

      saveUser(data);

      setAuthLoading(false);
      return { success: true, user: data };
    } catch (error) {
      console.error('Credential Login Error:', error);
      setAuthError(error.response?.data?.message || 'Failed to log in.');
      setAuthLoading(false);
      return { success: false };
    }
  };

  // -------------------------------------------------------------
  // LOGIN USING KEYCLOAK
  // -------------------------------------------------------------
  const loginWithKeycloak = async ({ access_token, keycloak_id, isAdmin }) => {
    if (!access_token) {
      return { success: false, message: 'No access token provided' };
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const endpoint = isAdmin
        ? `${apiPath}/authentication/keycloak/token/`
        : `${apiPath}/authentication/esecure/token/`;

      const payload = isAdmin ? { access_token, role: 'Employee' } : { access_token, keycloak_id };

      const { data } = await axios.post(endpoint, payload);

      // Multiple roles — defer saveUser until the user picks one
      if (Array.isArray(data) && data.length > 1) {
        localStorage.setItem('multiple_roles', JSON.stringify(data));
        setMultipleRoles(data);
        return { success: true, user: data };
      }

      // Single role (array of 1 or plain object)
      const normalized = Array.isArray(data) ? data[0] : data;
      saveUser(normalized);

      return { success: true, user: data };
    } catch (err) {
      const res = err.response;
      const msg = res?.data?.message || 'Failed to Login';

      // Enrollment
      if (!isAdmin && res?.status === 404 && msg.includes('No user found')) {
        setAuthError(msg);
        return { success: false, redirectToEnrollment: true };
      }

      // OAuth validation errors
      if (res?.data?.error_description) {
        localStorage.setItem('validation', res.data.error_description);
      }

      setAuthError(msg);
      return { success: false, message: msg };
    } finally {
      setAuthLoading(false);
    }
  };

  // -------------------------------------------------------------
  // SAVE USER TO LOCALSTORAGE + STATE
  // -------------------------------------------------------------
  const saveUser = (data) => {
    const userObj = Array.isArray(data) ? data[0] : data;

    setUser(userObj);
    localStorage.setItem('user_data', JSON.stringify(userObj));

    if (userObj?.user_id) localStorage.setItem('user_id', String(userObj.user_id));
    if (userObj?.role_name) localStorage.setItem('user_role', userObj.role_name);
    if (userObj?.role_id) localStorage.setItem('user_role_id', String(userObj.role_id));
    if (userObj?.keycloak_id) localStorage.setItem('keycloak_id', userObj.keycloak_id);
    if (userObj?.username) localStorage.setItem('user_username', userObj.username);
    if (userObj?.first_name) localStorage.setItem('user_first_name', userObj.first_name);
    if (userObj?.last_name) localStorage.setItem('user_last_name', userObj.last_name);
    if (userObj?.email) localStorage.setItem('user_email', userObj.email);
    if (userObj?.company_id) localStorage.setItem('user_company', String(userObj.company_id));
    if (userObj?.company_name) localStorage.setItem('user_company_name', userObj.company_name);
    if (userObj?.company_user_group) localStorage.setItem('user_company_group', userObj.company_user_group);

    setAuthHeaders({
      authToken: userObj?.key,
      userID: userObj?.user_id,
      roleID: userObj?.role_id,
    });
  };

  const switchRole = (roleObject) => {
    const currentUserData = JSON.parse(localStorage.getItem('user_data')) || {};

    const updated = {
      ...currentUserData,
      // spread all fields from the selected role so nothing is lost
      ...roleObject,
      // always prefer the new token if provided
      key: roleObject.key || currentUserData.key,
    };

    setUser(updated);
    localStorage.setItem('user_data', JSON.stringify(updated));
    localStorage.setItem('user_id', String(roleObject.user_id));
    localStorage.setItem('user_role', roleObject.role_name);
    localStorage.setItem('user_role_id', String(roleObject.role_id));
    localStorage.setItem('user_company', String(roleObject.company_id));
    localStorage.setItem('user_company_name', roleObject.company_name || '');
    if (roleObject.key) localStorage.setItem('user_token', roleObject.key);

    setAuthHeaders({
      authToken: updated.key,
      userID: roleObject.user_id,
      roleID: roleObject.role_id,
    });
  };

  // -------------------------------------------------------------
  // LOGOUT
  // -------------------------------------------------------------
  const logout = () => {
    localStorage.clear();
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        authLoading,
        authError,
        loginWithCredentials,
        loginWithKeycloak,
        logout,
        switchRole,
        multipleRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
